import React, { type JSX } from 'react';
import { Box, Stack, Button, Radio, Divider, CircularProgress, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CampaignIcon from '@mui/icons-material/Campaign';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { EmptyState } from '@brightlayer-ui/react-components';
import { CustomTransComponent } from 'components';
import { LoadGroupsList } from './LoadGroupsList';
import { DevicesList } from './DevicesList';
import { SelectTriggerEvent } from './SelectTriggerEvent';
import { DeviceLoadHierarchy } from './DeviceLoadHierarchy';

const toggleIdInList = (prev: any, id: any): any => {
    const current = Array.isArray(prev) ? prev : [];
    if (current.includes(id)) {
        return current.filter((x: any) => x !== id);
    }
    return [...current, id];
};

const toggleNodeInList = (prev: any, id: string, data: any): any => {
    if (prev.find((n: any) => n.id === id)) {
        return prev.filter((n: any) => n.id !== id);
    }
    return [...prev, data];
};

export const SaveActionLabel = ({ isEditing }: { isEditing: boolean }): JSX.Element => (
    <CustomTransComponent translationKey={isEditing ? 'COMMON:UPDATE' : 'COMMON:SAVE_LABEL'} />
);

type ModalTargetSelectorProps = {
    modalTriggerType: string;
    modalSelectedLoadId: any;
    handleModalLoadSelect: (loadNode: any) => void;
    setModalSelectedLoadId: any;
    setModalSelectedNodes: any;
};

const buildLoadGroupSelectHandler =
    (setModalSelectedLoadId: any, setModalSelectedNodes: any): ((loadGroupId: string, groupData: any) => void) =>
    (loadGroupId: string, groupData: any): void => {
        setModalSelectedLoadId((prev: any) => toggleIdInList(prev, loadGroupId));
        setModalSelectedNodes((prev: any) => toggleNodeInList(prev, loadGroupId, groupData));
    };

const buildDeviceSelectHandler =
    (setModalSelectedLoadId: any, setModalSelectedNodes: any): ((deviceId: string, deviceData: any) => void) =>
    (deviceId: string, deviceData: any): void => {
        setModalSelectedLoadId((prev: any) => toggleIdInList(prev, deviceId));
        setModalSelectedNodes((prev: any) => toggleNodeInList(prev, deviceId, deviceData));
    };

export const ModalTargetSelector = (props: ModalTargetSelectorProps): JSX.Element => {
    const { modalTriggerType, modalSelectedLoadId, handleModalLoadSelect } = props;
    if (modalTriggerType === 'loads') {
        return (
            <DeviceLoadHierarchy
                selectionType="checkbox"
                selectedLoadId={modalSelectedLoadId}
                onLoadSelect={handleModalLoadSelect}
            />
        );
    }
    if (modalTriggerType === 'loadGroups') {
        return (
            <LoadGroupsList
                selectionType="checkbox"
                selectedLoadGroupId={modalSelectedLoadId}
                onLoadGroupSelect={buildLoadGroupSelectHandler(
                    props.setModalSelectedLoadId,
                    props.setModalSelectedNodes
                )}
            />
        );
    }
    if (modalTriggerType === 'devices') {
        return (
            <DevicesList
                selectionType="checkbox"
                selectedDeviceId={modalSelectedLoadId}
                onDeviceSelect={buildDeviceSelectHandler(props.setModalSelectedLoadId, props.setModalSelectedNodes)}
            />
        );
    }
    return <></>;
};

type ActionRadioItemProps = { actionItem: any };

const ActionRadioItem = ({ actionItem }: ActionRadioItemProps): JSX.Element => (
    <React.Fragment>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <PowerSettingsNewIcon sx={{ fontSize: 20 }} />
                <Typography>{actionItem.displayName}</Typography>
            </Stack>
            <Radio value={actionItem.key} />
        </Stack>
        <Divider orientation="horizontal" flexItem />
    </React.Fragment>
);

const DEVICE_ACTION_NAMES = ['Turn On Device', 'Turn Off Device'];

const getDeviceActionItems = (allActions: any): any[] => {
    const data = allActions?.data ?? [];
    return data.filter((actionItem: any) => DEVICE_ACTION_NAMES.includes(actionItem.displayName)).reverse();
};

type ModalActionRadiosProps = {
    modalTriggerType: string;
    allActions: any;
    isDynamicActionsLoading: boolean;
    isDynamicActionsFetching: boolean;
    dynamicActionsData: any;
};

const ModalActionRadiosLoading = (): JSX.Element => (
    <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
    </Box>
);

const renderActionItems = (items: any): any => {
    const safeItems = items ?? [];
    return safeItems.map((actionItem: any) => <ActionRadioItem key={actionItem.key} actionItem={actionItem} />);
};

export const ModalActionRadios = (props: ModalActionRadiosProps): JSX.Element => {
    const { modalTriggerType, allActions, isDynamicActionsLoading, isDynamicActionsFetching, dynamicActionsData } =
        props;
    if (modalTriggerType === 'devices') {
        return <>{renderActionItems(getDeviceActionItems(allActions))}</>;
    }
    if (isDynamicActionsLoading || isDynamicActionsFetching) {
        return <ModalActionRadiosLoading />;
    }
    return <>{renderActionItems(dynamicActionsData?.data)}</>;
};

type TriggerColumnBodyProps = {
    theme: any;
    t: any;
    triggerType: string;
    selectedLoadId: any;
    handleTriggerLoadSelect: (loadNode: any) => void;
    setSelectedLoadId: any;
    setTempLoadGroupActions: any;
};

export const TriggerColumnBody = (props: TriggerColumnBodyProps): JSX.Element => {
    const {
        theme,
        t,
        triggerType,
        selectedLoadId,
        handleTriggerLoadSelect,
        setSelectedLoadId,
        setTempLoadGroupActions,
    } = props;
    if (!triggerType) {
        return (
            <EmptyState
                icon={<BoltIcon sx={{ fontSize: 100, color: theme.palette.action.active }} />}
                title={t('WIDGETS:BALANCING.TRIGGER_EVENT_ICON_HELPER')}
            />
        );
    }
    if (triggerType === 'loads') {
        return (
            <Box>
                <DeviceLoadHierarchy selectedLoadId={selectedLoadId} onLoadSelect={handleTriggerLoadSelect} />
            </Box>
        );
    }
    if (triggerType === 'loadGroups') {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <LoadGroupsList
                    selectedLoadGroupId={selectedLoadId}
                    onLoadGroupSelect={(loadGroupId): void => {
                        setSelectedLoadId(loadGroupId);
                        setTempLoadGroupActions([]);
                    }}
                />
            </Box>
        );
    }
    return <Box></Box>;
};

type EventColumnBodyProps = {
    theme: any;
    t: any;
    selectedLoadId: any;
    triggerType: string;
    handleLoadGroupEventChange: (...args: any[]) => void;
    setHasOnSetChecked: (v: boolean) => void;
    initialHydratedEventsRef: any;
    selectedDeviceId: any;
    selectedDeviceTypeId: any;
    deviceTypeIds: string[];
};

export const EventColumnBody = (props: EventColumnBodyProps): JSX.Element => {
    const {
        theme,
        t,
        selectedLoadId,
        triggerType,
        handleLoadGroupEventChange,
        setHasOnSetChecked,
        initialHydratedEventsRef,
        selectedDeviceId,
        selectedDeviceTypeId,
        deviceTypeIds,
    } = props;
    if (!selectedLoadId) {
        return (
            <EmptyState
                icon={<CampaignIcon sx={{ fontSize: 100, color: theme.palette.action.active }} />}
                title={t('WIDGETS:BALANCING.TRIGGER_EVENT_HELPER')}
            />
        );
    }
    if (triggerType === 'loads') {
        return (
            <Box sx={{ overflow: 'auto' }}>
                <SelectTriggerEvent
                    onTriggerEventChange={handleLoadGroupEventChange}
                    onCheckboxStateChange={(hasChecked): void => setHasOnSetChecked(hasChecked)}
                    initialTriggerEvents={initialHydratedEventsRef.current}
                    selectedId={selectedLoadId}
                    deviceId={selectedDeviceId}
                    deviceTypeId={selectedDeviceTypeId}
                    deviceTypeIds={deviceTypeIds}
                />
            </Box>
        );
    }
    return (
        <Box sx={{ overflow: 'auto' }}>
            <SelectTriggerEvent
                onTriggerEventChange={handleLoadGroupEventChange}
                onCheckboxStateChange={(hasChecked): void => setHasOnSetChecked(hasChecked)}
                initialTriggerEvents={initialHydratedEventsRef.current}
                isLoadGroup={true}
                deviceTypeIds={deviceTypeIds}
            />
        </Box>
    );
};

type AppBarActionsProps = {
    isSaving: boolean;
    isCreatingAutomation: boolean;
    isUpdatingAutomation: boolean;
    isEditMode: boolean;
    automationDetails: any;
    snapshotRef: any;
    isDirty: boolean;
    automationName: string;
    createdActions: any[];
    previousRoute: any;
    navigate: any;
    handleSaveAutomation: () => void | Promise<void>;
};

const isSaveButtonDisabled = (props: AppBarActionsProps): boolean => {
    if (props.isCreatingAutomation || props.isUpdatingAutomation) {
        return true;
    }
    if (props.isEditMode) {
        const noChanges = !props.automationDetails?.data || !props.snapshotRef.current || !props.isDirty;
        if (noChanges) {
            return true;
        }
    }
    if (!props.automationName.trim()) {
        return true;
    }
    return props.createdActions.length === 0;
};

const SaveButtonLabel = ({ isSavingNow }: { isSavingNow: boolean }): JSX.Element => {
    if (isSavingNow) {
        return <CircularProgress size={24} color="inherit" />;
    }
    return <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />;
};

export const AppBarActions = (props: AppBarActionsProps): JSX.Element => {
    const handleCancel = (): void => {
        if (props.previousRoute) {
            props.navigate(props.previousRoute, { state: { subTab: 'balancing' } });
            return;
        }
        props.navigate(-1);
    };
    const onSaveClick = (): void => {
        void props.handleSaveAutomation();
    };
    return (
        <Stack direction="row" spacing={1}>
            <Button variant="outlined" disabled={props.isSaving} onClick={handleCancel}>
                <CustomTransComponent translationKey={'COMMON:CANCEL'} />
            </Button>
            <Button
                variant="contained"
                onClick={onSaveClick}
                disabled={isSaveButtonDisabled(props)}
                sx={{ '&:hover': { backgroundColor: 'primary.main' } }}
            >
                <SaveButtonLabel isSavingNow={props.isCreatingAutomation || props.isUpdatingAutomation} />
            </Button>
        </Stack>
    );
};
